import {FC} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Breadcrumbs, Button, Tab, Tabs, Typography} from "@mui/material";
import {Link, Navigate, Route, Routes, useNavigate, useParams} from "react-router-dom";
import {useAdminEntity} from "@buildwitt/shapeshift-common/v1/hooks";
import {IAdminUsersGroupsResponseEntry} from "@buildwitt/shapeshift-types";
import {EnrollmentTab} from "./Tabs/EnrollmentTab";
import {CoursesTab} from "./Tabs/CoursesTab";
import {Edit, PlaylistPlay, SummarizeOutlined} from "@mui/icons-material";
import {CertificationTab} from "@/pages/ManagerView/Pages/Playlists/AdminPlaylistLandingPage/Tabs/CertificationTab";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

interface IStyleProps {
}

const useStyles = makeStyles<IStyleProps>()((theme) => ({
	adminPlaylistLandingPage: {
		marginLeft: "auto",
		marginRight: "auto"
	},
	breadcrumbs: {
		marginBottom: "25px",
		"& svg": {
			marginRight: theme.spacing(1),
			top: "2px",
			position: "relative",
			fontSize: "18px"
		}
	},
	adminBanner: {
		position: "relative",
		display: "flex",
		flexDirection: "column",
		width: "100%",
		height: "auto",
		borderRadius: "5px",
		overflow: "hidden",
		marginBottom: "12px"
	},
	adminBannerTitle: {
		position: "relative",
		fontSize: "34px",
		lineHeight: "1.1",
		fontWeight: "600",
		letterSpacing: "0.25px",
		alignSelf: "end",
		margin: "12px 0 0",
		zIndex: 2
	},
	adminPlaylistButton: {
		marginTop: "-2px"
	},
	adminVideo: {
		width: "100%",
		height: "100%"
	},
	adminBackgroundImage: {
		position: "absolute",
		left: 0,
		top: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover"
	},
	adminPlaylistLabel: {
	},
	adminBadge: {
		padding: theme.spacing(0.25, 1.25, 0.35),
		marginBottom: theme.spacing(0.5)
	},
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(3.5),
		backgroundColor: theme.palette.background.paper,
		border: 0,
		borderRadius: theme.borderRadius.small
	},
	titleRow :{
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	playlistBox: {
	},
	adminTrainingFeedModule: {
		minWidth: "340px",
		maxWidth: "340px"
	},
	playlistList: {
		marginTop: theme.spacing(1),
		flexGrow: 1
	},
	playlistListItem: {
		fontSize: "20px"
	},
	playlistListLabel: {
		fontSize: "14px",
		marginTop: "8px",
		color: theme.palette.grey[600]
	},
	hr: {
		borderTop: 0,
		borderColor: theme.palette.grey[50]
	},
	adminTabs: {
		borderBottom: 0,
		"& [class*=indicator]": {
			bottom: "4px"
		}
	},
	adminTab: {
		fontSize: "14px",
		fontWeight: "600",
		minHeight: "40px",
		minWidth: "85px",
		padding: 0,
		paddingLeft: "15px",
		paddingRight: "15px",
		letterSpacing: "0.4px",
		textTransform: "uppercase"
	}
}));

interface IExtendedAdminUsersGroupsResponseEntry extends IAdminUsersGroupsResponseEntry {
	certificationId?: string;
}

export const AdminPlaylistLandingPage: FC = () => {
	const {playlistId} = useParams();
	const entityId = playlistId ? parseInt(playlistId) : -1;
	const {entity: playlist} = useAdminEntity<IExtendedAdminUsersGroupsResponseEntry, "group">("group", entityId, undefined, "en");
	const videoUrl = playlist?.videoUrl;
	const hasVideo = !!videoUrl;
	const {classes} = useStyles({hasVideo});
	const navigate = useNavigate();
	const {playlistBasedCerts} = useDevCycleSdk();

	const tabRoutes = [
		`/admin/playlists/${entityId}/enrollments`,
		`/admin/playlists/${entityId}/courses`,
		`/admin/playlists/${entityId}/certification`
	];

	const currentTab = tabRoutes.findIndex((route) =>
		location.pathname === (route)
	);

	const handleTabChange = (_: React.SyntheticEvent, newTab: number): void => {
		navigate(tabRoutes[newTab]);
	};

	return (
		<Box className={classes.adminPlaylistLandingPage}>
			<Breadcrumbs className={classes.breadcrumbs} separator="/" aria-label="breadcrumb">
				<Link to={"/admin/playlists"}><Typography variant="body1"><PlaylistPlay /> Playlists</Typography></Link>
				<Link to={`/admin/playlists/${playlistId}`}><Typography variant="body1"><SummarizeOutlined />{playlist?.title}</Typography></Link>
			</Breadcrumbs>

			<Box className={classes.titleRow}>
				<Typography variant="h3" className={classes.adminBannerTitle}>
					{playlist?.title}
				</Typography>
				<Link to="edit"><Button variant="outlined"><Edit /> EDIT PLAYLIST</Button></Link>
			</Box>
			<Box padding={2}/>
			{(currentTab !== -1) &&
				<Tabs className={classes.adminTabs} value={currentTab} onChange={handleTabChange} aria-label="report tabs">
					<Tab className={classes.adminTab} label="Enrollment" />
					<Tab className={classes.adminTab} label="Courses" />
					{playlistBasedCerts && playlist?.certificationId && <Tab className={classes.adminTab} label="Certification" />}
				</Tabs>
			}

			<Routes>
				<Route path="/" element={<Navigate to="enrollments" replace />} />
				<Route path="enrollments" element={<EnrollmentTab playlistId={entityId} />} />
				<Route path="courses" element={<CoursesTab playlistId={entityId} playlist={playlist} />} />
				<Route path="certification" element={<CertificationTab certificationId={playlist?.certificationId} />} />
			</Routes>
		</Box>
	);
}